import { createApi } from '@reduxjs/toolkit/query/react';

import { TAG_RACE } from '@config/tags';
import { SCHEDULE_RACES } from '@config/urls';
import { RootState } from '@store/index';

import { adaptRace } from '../adapters/races';

import { baseQuery } from './baseQuery';

import type { Race } from '../types';
import type { ApiRace } from '../types/apiRace';
import type { Lang } from '@assets/locales';

export type RaceArgs = {
  date: string;
  racecourseCode?: string;
  raceNumber: number;
  language: Lang;
};

// Define a service using a base URL and expected endpoints
export const raceApi = createApi({
  reducerPath: 'raceApi',
  tagTypes: [TAG_RACE],
  baseQuery,
  refetchOnFocus: true,
  endpoints: builder => ({
    getRace: builder.query<Race, RaceArgs>({
      query: ({ date, racecourseCode, raceNumber, language }) => ({
        url: `${SCHEDULE_RACES}/${date}/${racecourseCode}/${raceNumber}`,
        params: { language },
      }),
      transformResponse: (response: ApiRace): Race => {
        return adaptRace(response);
      },
    }),
  }),
});

export const getRaces = (
  state: RootState,
  date: string,
  racecourseCode: string,
  raceNumbers: number[],
  language: Lang
) => {
  return raceNumbers
    .map(raceNumber => {
      const selectRace = raceApi.endpoints.getRace.select({
        date,
        racecourseCode,
        raceNumber,
        language,
      });

      return selectRace(state).data;
    })
    .filter((race): race is Race => race !== undefined);
};

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetRaceQuery, useLazyGetRaceQuery } = raceApi;
